import {
  useState,
  useRef,
  useEffect,
  useCallback,
  HTMLInputTypeAttribute,
} from "react";
import {
  POST_PRE_SIGNED_URL_MUTATION,
  PreSignedUrlData,
} from "../../../api/Requests";
import { useMutation } from "@apollo/client";
import { myToastError } from "../../../util/myToast";
import { MyLittleLoader } from "../../../util/myLoader";
import { RenderPublishedText } from "./helpers";

interface inputState {
  value: string;
  setter: (val: any) => void;
}

interface RenderEditItemProps {
  editModeEnabled?: boolean;
  inputItem: inputState;
  enableTextArea?: boolean;
  saveAction?: (itemOverride?: any) => void;
  placeholder?: string;
  inputType?: HTMLInputTypeAttribute;
  accentColor?: string;
}

export function RenderEditItem({
  editModeEnabled = true,
  inputItem,
  enableTextArea = false,
  saveAction,
  placeholder,
  inputType,
  accentColor,
}: RenderEditItemProps) {
  const [editTarget, setEditTarget] = useState(false);

  useEffect(() => {
    const textAreas =
      document.querySelectorAll<HTMLTextAreaElement>(".editTextArea");
    textAreas.forEach(ta => {
      ta.style.height = "auto";
      ta.style.height = `${ta.scrollHeight}px`;
    });
  }, [inputItem?.value, editTarget]);

  function toggleOffAndSave() {
    setEditTarget(false);
    // Trigger additional save action if provided
    saveAction?.();
  }

  // The ghostInput will help keep the input at the proper width during editing
  return editModeEnabled ? (
    <div
      onBlur={() => toggleOffAndSave()}
      className={`editSelector ${editTarget ? "editActive" : "preEditSelector"}`}
      onClick={() => setEditTarget(true)}
    >
      {editTarget ? (
        <div>
          {enableTextArea ? (
            <textarea
              name="editTextArea"
              className="editTextArea"
              placeholder="Enter Text"
              defaultValue={inputItem?.value}
              onChange={val => inputItem?.setter(val.target.value)}
              autoFocus
            />
          ) : (
            <input
              name="editInput"
              placeholder="Enter Text"
              defaultValue={inputItem?.value}
              onChange={val => inputItem?.setter(() => val.target.value)}
              autoFocus
              type={inputType}
            />
          )}
        </div>
      ) : inputItem?.value ? (
        <RenderPublishedText
          text={inputItem?.value}
          accentColor={accentColor}
        />
      ) : (
        <div className="editActive editEmpty">
          {placeholder ? "Edit " + placeholder : "Click to Edit"}
        </div>
      )}
      <div className="ghostInput">{inputItem?.value}</div>
    </div>
  ) : (
    <RenderPublishedText text={inputItem?.value} accentColor={accentColor} />
  );
}

async function uploadImage(url: string, file: File) {
  try {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
    });
    return response.ok;
  } catch (error) {
    console.error("Error uploading image", error);
    return false;
  }
}

interface RenderEditImageProps {
  editModeEnabled?: boolean;
  inputSrc: inputState;
  altText?: string;
  editToolsOutside?: boolean;
  itemClass?: string;
  setImgAsBackground?: boolean;
  saveAction?: (imgOverride?: any) => void;
  imageSizeRecommendation?: { width: number; height: number };
}

export function RenderEditImage({
  editModeEnabled = true,
  inputSrc,
  altText = "",
  editToolsOutside = false,
  itemClass = "",
  setImgAsBackground = false,
  saveAction = undefined,
  imageSizeRecommendation,
}: RenderEditImageProps) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [editTarget, setEditTarget] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [postPreSignedUrl, { data, loading, error }] =
    useMutation<PreSignedUrlData>(POST_PRE_SIGNED_URL_MUTATION);

  const handleSaveAndClose = useCallback(
    (fileUrl: string) => {
      console.log("handleSaveAndClose", fileUrl);
      inputSrc?.setter(fileUrl);
      setEditTarget(false);
      // Trigger additional save action if provided
      saveAction?.(fileUrl);
    },
    [inputSrc, saveAction],
  );

  useEffect(() => {
    // Handle image upload
    if (data && !loading && !error && !imageUploading && imageFile) {
      if (data.image?.presignedUrl && data.image?.fullKey) {
        setImageUploading(true);
        uploadImage(data.image.presignedUrl, imageFile).then(success => {
          if (success) {
            const urlWithFullKey = `https://d34pl9m09oi7oj.cloudfront.net/${data.image.fullKey}`;
            handleSaveAndClose(urlWithFullKey);
          } else {
            myToastError("Image upload failed");
          }
          setImageUploading(false);
          setImageFile(null);
        });
      } else {
        myToastError("Image upload error");
      }
    }
  }, [
    data,
    loading,
    error,
    editTarget,
    handleSaveAndClose,
    imageUploading,
    imageFile,
  ]);

  function startPostPreSignedUrl(myFile: File) {
    // Fetch pre-signed URL (handled by useEffect)
    postPreSignedUrl({
      variables: {
        input: {
          myKey: myFile.name, // example: example-key2.png
          myContentType: myFile.type, // example: image/png
          footPrint: myFile.size, // bytes
        },
      },
    });
    setImageFile(myFile);
  }

  function handleRemoveAndClose() {
    setEditTarget(false);
    inputSrc?.setter("");
    // Trigger additional save action if provided
    saveAction?.("empty");
  }

  function handleInputFile() {
    if (inputFileRef && inputFileRef.current !== null) {
      inputFileRef.current.click();
    }
  }

  function handleOpenEdit(hasImage = false) {
    if (hasImage) {
      setEditTarget(val => !val);
    } else {
      handleInputFile();
    }
  }
  return (
    <div className={itemClass}>
      {(loading || imageUploading) && <MyLittleLoader />}
      {editModeEnabled && !loading && !imageUploading ? (
        <div className="editImage">
          <input
            type="file"
            ref={inputFileRef}
            accept="image/*"
            onChange={event => {
              const myFile = event?.target?.files?.item(0);
              if (myFile) {
                if (imageSizeRecommendation) {
                  const img = new Image();
                  img.src = URL.createObjectURL(myFile);
                  img.onload = () => {
                    if (
                      img.width < imageSizeRecommendation.width ||
                      img.height < imageSizeRecommendation.height
                    ) {
                      myToastError(
                        `${altText ?? "Image"} dimensions should be at least ${imageSizeRecommendation.width} (width) x ${imageSizeRecommendation.height} (height)`,
                      );
                    } else {
                      startPostPreSignedUrl(myFile);
                    }
                  };
                } else {
                  startPostPreSignedUrl(myFile);
                }
              } else {
                myToastError("Issue retrieving image");
              }
            }}
            style={{ display: "none" }}
          />
          {editTarget && !editToolsOutside && (
            <button
              type="button"
              className="editCloseButton editImageAddButton"
              onClick={() => handleInputFile()}
            >
              Upload New Image
            </button>
          )}
          {editTarget && !editToolsOutside && (
            <button
              type="button"
              className="editCloseButton editImageRemoveButton"
              onClick={() => handleRemoveAndClose()}
            >
              Clear Image
            </button>
          )}
          {/* eslint-disable-next-line */}
          <img
            className={`editSelector ${editTarget && !setImgAsBackground ? "editActive" : "preEditSelector"}`}
            onClick={() =>
              handleOpenEdit(!!inputSrc?.value && !setImgAsBackground)
            }
            src={!setImgAsBackground ? inputSrc?.value : ""}
            alt={
              !setImgAsBackground
                ? `🖼️ Add image for "${altText}"`
                : `🖼️ Change image for "${altText}"`
            }
          />
          {(editTarget || setImgAsBackground) && editToolsOutside && (
            <button
              type="button"
              className="editCloseButton"
              onClick={() => handleInputFile()}
            >
              Upload New Image
            </button>
          )}
          {(editTarget || setImgAsBackground) && editToolsOutside && (
            <button
              type="button"
              className="editCloseButton"
              onClick={() => handleRemoveAndClose()}
            >
              Clear Image
            </button>
          )}
        </div>
      ) : (
        inputSrc?.value && (
          <img
            src={inputSrc?.value}
            alt={altText}
            style={{ opacity: setImgAsBackground ? 0 : 1 }}
          />
        )
      )}
    </div>
  );
}
